import type { ProviderUserRoleLiteral } from '@multiplier/lib-shared-types';
import type { ReactNode } from 'react';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useUser } from '@/hooks/queries/use-user';

interface OrganizationContextType {
  selectedOrganizationId: string | null;
  setSelectedOrganizationId: (id: string) => void;
  availableOrganizations: Array<{ id: string; name: string; role: ProviderUserRoleLiteral }>;
  currentUserRole: ProviderUserRoleLiteral | undefined;
  currentUserId: string | undefined;
  isLoading: boolean;
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

export const ORG_SELECTION_STORAGE_KEY = 'selectedOrganizationId';

interface OrganizationProviderProps {
  children: ReactNode;
}

export const OrganizationProvider = ({ children }: OrganizationProviderProps) => {
  const { data: userData, isLoading: isLoadingUser } = useUser();
  const [selectedOrganizationId, setSelectedOrganizationIdState] = useState<string | null>(null);

  const availableOrganizations = useMemo(() => {
    // TODO: currently shows ALL organizations the user belongs to across all products.
    // Users who are also members of corporate orgs will see those orgs in the tax-frontend
    // org switcher. Fix: filter availableOrganizations by org type once platform-service
    // exposes a `type`/`product` field on organizations. See EnsureUserOrganizationService.ts
    // for the corresponding API TODO.
    const orgs = userData?.organizationUser?.organizations ?? [];
    return orgs.map((org) => ({
      id: org.id,
      name: org.organizationName ?? 'Unnamed Organization',
      // Cast at API boundary - the API returns string but values are always valid role literals
      role: org.role as ProviderUserRoleLiteral,
    }));
  }, [userData]);

  const currentUserId = userData?.organizationUser?.id;

  const currentUserRole = useMemo(() => {
    if (!selectedOrganizationId) {
      return;
    }
    const org = availableOrganizations.find((o) => o.id === selectedOrganizationId);
    return org?.role;
  }, [selectedOrganizationId, availableOrganizations]);

  // Initialize selected organization from localStorage or default to first
  useEffect(() => {
    if (availableOrganizations.length === 0) {
      return;
    }

    // Try to restore from localStorage
    const stored = localStorage.getItem(ORG_SELECTION_STORAGE_KEY);
    if (stored && availableOrganizations.some((org) => org.id === stored)) {
      setSelectedOrganizationIdState(stored);
    } else {
      // Default to first organization
      setSelectedOrganizationIdState(availableOrganizations[0].id);
    }
  }, [availableOrganizations]);

  const setSelectedOrganizationId = useCallback((id: string) => {
    setSelectedOrganizationIdState(id);
    localStorage.setItem(ORG_SELECTION_STORAGE_KEY, id);
  }, []);

  const value = useMemo(
    () => ({
      selectedOrganizationId,
      setSelectedOrganizationId,
      availableOrganizations,
      currentUserRole,
      currentUserId,
      isLoading: isLoadingUser,
    }),
    [
      selectedOrganizationId,
      setSelectedOrganizationId,
      availableOrganizations,
      currentUserRole,
      currentUserId,
      isLoadingUser,
    ]
  );

  return <OrganizationContext.Provider value={value}>{children}</OrganizationContext.Provider>;
};

export const useOrganization = (): OrganizationContextType => {
  const context = useContext(OrganizationContext);
  if (!context) {
    throw new Error('useOrganization must be used within OrganizationProvider');
  }
  return context;
};
